import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  BriefcaseBusiness,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  ClipboardCheck,
  FileText,
  GraduationCap,
  MapPin,
  Quote,
  Search,
  ShieldCheck,
  Star,
  TrainFront,
  UserRound,
  Users,
} from "lucide-react";
import Seo from "../components/Seo";
import ResponsiveImage from "../components/ResponsiveImage";
import HeroBackdrop from "../components/HeroBackdrop";

const mainRoutes = [
  {
    icon: TrainFront,
    eyebrow: "Understand the role",
    title: "What do train drivers do?",
    body: "Explore the day-to-day reality of train driving, including shifts, safety responsibility, concentration, disruption, training and competence.",
    to: "/what-does-a-train-driver-do",
    cta: "Explore the role",
  },
  {
    icon: ClipboardCheck,
    eyebrow: "Understand the route",
    title: "How to become a train driver",
    body: "Follow the UK recruitment journey from eligibility and applications through assessments, interviews, medical checks and operator training.",
    to: "/become-a-driver",
    cta: "View the step-by-step guide",
  },
  {
    icon: UserRound,
    eyebrow: "Hear from drivers",
    title: "Journeys to the driving seat",
    body: "Read real career stories from current train drivers, including their previous roles, recruitment experiences and advice for applicants.",
    to: "/my-train-driver-journey",
    cta: "Read driver journeys",
  },
];

const audienceRoutes = [
  {
    icon: ShieldCheck,
    title: "Service leavers",
    body: "Translate military experience into credible evidence for train driver applications and safety-critical recruitment.",
    to: "/service-leavers",
    cta: "Visit the service leavers hub",
  },
  {
    icon: GraduationCap,
    title: "Future drivers",
    body: "For under-18s who want to build operational knowledge and prepare properly before becoming eligible to apply.",
    to: "/become-a-train-driver-at-18",
    cta: "Visit the Future Drivers hub",
  },
  {
    icon: Search,
    title: "Current vacancies",
    body: "Search for current UK railway and trainee train driver opportunities.",
    to: "/careers",
    cta: "Search current careers",
  },
  {
    icon: ClipboardCheck,
    title: "Check eligibility",
    body: "Review the typical entry, medical, travel and practical requirements before you begin applying.",
    to: "/start-your-journey#eligibility",
    cta: "Check your eligibility",
  },
  {
    icon: BriefcaseBusiness,
    title: "Products and services",
    body: "Explore all application support, assessment preparation, interview tools, courses and career packages.",
    to: "/products",
    cta: "View all products and services",
  },
  {
    icon: FileText,
    title: "Guides and articles",
    body: "Read practical articles covering train driver careers, recruitment, assessments, interviews and railway knowledge.",
    to: "/blog",
    cta: "Visit the blog",
  },
];

const journeySteps = [
  {
    number: "01",
    title: "Understand the job",
    body: "Learn what train drivers actually do and decide whether the work, responsibility and lifestyle suit you.",
    to: "/what-does-a-train-driver-do",
  },
  {
    number: "02",
    title: "Check the route",
    body: "Understand the usual entry requirements and the stages used during trainee train driver recruitment.",
    to: "/become-a-driver",
  },
  {
    number: "03",
    title: "Build your readiness",
    body: "Develop stronger knowledge, evidence and preparation before a suitable vacancy becomes available.",
    to: "/products",
  },
  {
    number: "04",
    title: "Find opportunities",
    body: "Search current roles and apply directly to the recruiting train or freight operating company.",
    to: "/careers",
  },
];

const customerReviews = [
  {
    name: "Gordon R",
    title: "Train Driver Foundation Support",
    rating: 5,
    text: "The help and support I received from TDF has been first class. They assisted me with my CV, applications, and interview preparation throughout the process. Following their guidance, I’ve gone on to pursue my career in Train Driving.",
  },
  {
    name: "Andy H",
    title: "Train Driving Course",
    rating: 5,
    text: "The course gave me a fuller understanding of the daily requirements of the role and helped me identify examples from my Armed Forces experience that demonstrate the non-technical skills operators look for.",
  },
  {
    name: "Aaron",
    title: "Interview Simulator",
    rating: 5,
    text: "The interview simulator has been really helpful. It pushes me to improve, identifies where my answers need work and explains how I can make them stronger.",
  },
  {
    name: "Tom",
    title: "Operational Railway Insight",
    rating: 5,
    text: "Great and helpful information. Great insight into the specific requirements for operational railway roles.",
  },
  {
    name: "Daniel M",
    title: "CV Review Service",
    rating: 5,
    text: "I used the CV review service for a trainee driver application and it helped me make the sift and get the job. The replies were prompt and included useful guidance.",
  },
];

const faqs = [
  {
    q: "What should I read first?",
    a: "Start with What Do Train Drivers Do? if you are still exploring the career. Move to How to Become a Train Driver when you are ready to understand eligibility and recruitment.",
  },
  {
    q: "Do I need railway experience?",
    a: "Not always. Many trainee train driver vacancies accept external applicants. Operators generally look for evidence of safety awareness, concentration, reliability, communication and the ability to follow rules.",
  },
  {
    q: "Where can I find current vacancies?",
    a: "Use the Careers section to search current railway opportunities and identify operators recruiting in your area.",
  },
  {
    q: "Where can I see all preparation options?",
    a: "The Products and Services page brings together all available application support, assessment preparation, interview tools, courses and packages.",
  },
];

export default function Home() {
  const [activeReview, setActiveReview] = useState(0);

  const showPreviousReview = () => {
    setActiveReview((current) =>
      current === 0 ? customerReviews.length - 1 : current - 1
    );
  };

  const showNextReview = () => {
    setActiveReview((current) =>
      current === customerReviews.length - 1 ? 0 : current + 1
    );
  };

  useEffect(() => {
    const existingTrustpilotScript = document.querySelector(
      'script[src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"]'
    );

    if (!existingTrustpilotScript) {
      const script = document.createElement("script");

      script.src =
        "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
      script.type = "text/javascript";
      script.async = true;

      document.head.appendChild(script);
    }
  }, []);

  const organisationSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: "Train Driver Foundation",
    url: "https://traindriverfoundation.com/",
    logo: "https://traindriverfoundation.com/logo.png",
    description:
      "Independent UK train driver career guidance and recruitment preparation, including eligibility guidance, application support, assessment preparation, interview tools and rail-focused learning.",
    areaServed: {
      "@type": "Country",
      name: "United Kingdom",
    },
    sameAs: [
      "https://traindriverpsychometrics.co.uk/",
      "https://railwaycareers.co.uk/",
    ],
    knowsAbout: [
      "UK train driver careers",
      "Trainee train driver recruitment",
      "Train driver eligibility",
      "Train driver applications",
      "Train driver psychometric assessments",
      "Train driver interviews",
      "Railway non-technical skills",
      "Safety-critical recruitment",
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Train Driver Foundation",
    url: "https://traindriverfoundation.com/",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  return (
    <div className="bg-white">
      <Seo
        title="Train Driver Careers, Guidance and Recruitment Preparation"
        description="Understand what train drivers do, learn how to become a train driver, check eligibility, read real driver journeys and find current UK railway opportunities."
        path="/"
        jsonLd={[organisationSchema, websiteSchema, faqSchema]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#07111F] text-white">
        <HeroBackdrop alt="UK passenger train and railway platform" />

        <div className="absolute inset-0 bg-gradient-to-r from-[#07111F] via-[#07111F]/95 to-[#07111F]/65" />
        <div className="tdf-hero-glow-red" aria-hidden="true" />
        <div className="tdf-hero-glow-blue" aria-hidden="true" />

        <div className="relative mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-center lg:px-8 lg:py-28">
          <div>
            <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-200 backdrop-blur-sm">
              <TrainFront className="h-4 w-4 text-[#E63B2E]" />
              Train Driver Foundation
            </p>

            <h1 className="tdf-heading max-w-4xl text-4xl leading-[1.05] text-white md:text-6xl lg:text-7xl">
              A clearer route into train driving.
            </h1>

            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-slate-200 md:text-xl">
              Understand the job, learn how recruitment works, check your
              eligibility and prepare for the journey into one of the
              railway&rsquo;s most responsible careers.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/what-does-a-train-driver-do"
                className="btn-pill btn-primary"
              >
                What do train drivers do?
                <ArrowRight className="h-4 w-4" />
              </Link>

              <Link
                to="/become-a-driver"
                className="btn-pill btn-outline-white"
              >
                How to become a train driver
              </Link>
            </div>

            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-300">
              {[
                "Independent guidance",
                "Real driver insight",
                "Structured preparation",
              ].map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-2"
                >
                  <Check className="h-4 w-4 text-[#16b9c8]" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          <aside className="rounded-3xl border border-white/12 bg-white/[0.07] p-6 shadow-2xl backdrop-blur-md md:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#16b9c8]">
              Start here
            </p>

            <h2 className="tdf-heading mt-3 text-2xl text-white md:text-3xl">
              What are you looking for?
            </h2>

            <div className="mt-6 space-y-3">
              {[
                {
                  label: "Understand the train driver role",
                  to: "/what-does-a-train-driver-do",
                  icon: TrainFront,
                },
                {
                  label: "Learn how to become a driver",
                  to: "/become-a-driver",
                  icon: ClipboardCheck,
                },
                {
                  label: "Check my eligibility",
                  to: "/start-your-journey#eligibility",
                  icon: ShieldCheck,
                },
                {
                  label: "Find current careers",
                  to: "/careers",
                  icon: Search,
                },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-4 transition hover:border-[#E63B2E]/70 hover:bg-white/[0.1]"
                  >
                    <span className="flex items-center gap-3 font-semibold text-white">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-[#16b9c8]">
                        <Icon className="h-5 w-5" />
                      </span>

                      {item.label}
                    </span>

                    <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-1 group-hover:text-white" />
                  </Link>
                );
              })}
            </div>
          </aside>
        </div>

        <div className="rail-accent" />
      </section>

      {/* MAIN ROUTES */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[#E63B2E]">
              Begin your research
            </p>

            <h2 className="tdf-heading text-3xl text-tdf-navy md:text-5xl">
              Start with the information that matters most.
            </h2>

            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Learn about the job, understand the route into it and hear from
              people who have already made the journey.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {mainRoutes.map((route) => {
              const Icon = route.icon;

              return (
                <article
                  key={route.title}
                  className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl md:p-8"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0F1B2E] text-white">
                    <Icon className="h-7 w-7" />
                  </div>

                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-[#E63B2E]">
                    {route.eyebrow}
                  </p>

                  <h3 className="tdf-heading mt-3 text-2xl text-tdf-navy">
                    {route.title}
                  </h3>

                  <p className="mt-4 flex-1 leading-relaxed text-slate-600">
                    {route.body}
                  </p>

                  <Link
                    to={route.to}
                    className="mt-7 inline-flex items-center gap-2 font-bold text-[#E63B2E] transition-all group-hover:gap-3"
                  >
                    {route.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SIMPLE JOURNEY */}
      <section className="bg-[#0F1B2E] py-20 text-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[#16b9c8]">
              The broad journey
            </p>

            <h2 className="tdf-heading text-3xl text-white md:text-5xl">
              From initial research to a live application.
            </h2>

            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              Recruitment varies between operators, but every applicant should
              understand the role, assess their readiness and prepare before a
              suitable vacancy opens.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {journeySteps.map((step) => (
              <Link
                key={step.number}
                to={step.to}
                className="group rounded-2xl border border-white/10 bg-white/[0.05] p-6 transition hover:border-[#E63B2E]/60 hover:bg-white/[0.08]"
              >
                <span className="tdf-heading text-4xl text-[#E63B2E]">
                  {step.number}
                </span>

                <h3 className="tdf-heading mt-5 text-xl text-white">
                  {step.title}
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                  {step.body}
                </p>

                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#16b9c8] transition-all group-hover:gap-3">
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE AND SITE DIRECTORY */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[#E63B2E]">
              Find your next step
            </p>

            <h2 className="tdf-heading text-3xl text-tdf-navy md:text-5xl">
              Guidance for your circumstances.
            </h2>

            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Choose the section that best matches your current situation or
              the information you need.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {audienceRoutes.map((route) => {
              const Icon = route.icon;

              return (
                <article
                  key={route.title}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-slate-50 p-6 transition duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-xl"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E63B2E]/10 text-[#E63B2E]">
                    <Icon className="h-6 w-6" />
                  </div>

                  <h3 className="tdf-heading mt-5 text-xl text-tdf-navy">
                    {route.title}
                  </h3>

                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">
                    {route.body}
                  </p>

                  <Link
                    to={route.to}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#E63B2E] transition-all group-hover:gap-3"
                  >
                    {route.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* DRIVER JOURNEYS FEATURE */}
      <section className="border-y border-slate-200 bg-slate-50 py-20">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <div className="relative">
            <div className="absolute -inset-4 rotate-2 rounded-3xl bg-[#16b9c8]/10" />

            <ResponsiveImage
              src="platform-sunset-manchester.jpg"
              alt="Train at a UK railway station platform"
              className="relative aspect-[4/3] w-full rounded-3xl object-cover shadow-xl"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </div>

          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[#E63B2E]">
              Real career stories
            </p>

            <h2 className="tdf-heading text-3xl text-tdf-navy md:text-5xl">
              Different backgrounds. Different routes. The same destination.
            </h2>

            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Current train drivers share how they entered the industry, what
              they experienced during recruitment and what they learned along
              the way.
            </p>

            <div className="mt-7 space-y-3">
              {[
                "Previous careers and transferable experience",
                "Applications, assessments and interviews",
                "Training and the move into the driving grade",
                "Practical advice for future applicants",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#E63B2E]" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <Link
              to="/my-train-driver-journey"
              className="btn-pill btn-primary mt-8"
            >
              Read train driver journeys
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[#E63B2E]">
              Customer feedback
            </p>

            <h2 className="tdf-heading text-3xl text-tdf-navy md:text-5xl">
              Support used at different stages of the journey.
            </h2>
          </div>

          <div className="mb-10 flex justify-center">
            <div
              className="trustpilot-widget"
              data-locale="en-GB"
              data-template-id="56278e9abfbbba0bdcd568bc"
              data-businessunit-id="69ee26a1e457e7fbbb62ef94"
              data-style-height="52px"
              data-style-width="100%"
              data-token="45f002b8-6222-4413-b048-11c5ea08d2fd"
            >
              <a
                href="https://uk.trustpilot.com/review/traindriverfoundation.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Trustpilot
              </a>
            </div>
          </div>

          <div className="relative mx-auto max-w-4xl">
            <article className="rounded-3xl border border-slate-200 bg-slate-50 p-7 shadow-sm md:p-10">
              <div className="mb-6 flex items-start justify-between gap-5">
                <div>
                  <div
                    className="mb-3 flex gap-1"
                    aria-label={`${customerReviews[activeReview].rating} out of 5 rating`}
                  >
                    {Array.from({
                      length: customerReviews[activeReview].rating,
                    }).map((_, index) => (
                      <Star
                        key={index}
                        className="h-5 w-5 fill-[#E63B2E] text-[#E63B2E]"
                      />
                    ))}
                  </div>

                  <h3 className="tdf-heading text-2xl text-tdf-navy">
                    {customerReviews[activeReview].title}
                  </h3>
                </div>

                <Quote className="h-10 w-10 shrink-0 text-slate-300" />
              </div>

              <p className="mb-7 text-base leading-relaxed text-slate-700 md:text-lg">
                “{customerReviews[activeReview].text}”
              </p>

              <div className="flex flex-col gap-4 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-bold text-tdf-navy">
                    {customerReviews[activeReview].name}
                  </p>

                  <p className="text-sm text-slate-500">
                    Customer feedback via Trustpilot
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={showPreviousReview}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-tdf-navy transition hover:border-[#E63B2E] hover:bg-[#E63B2E] hover:text-white"
                    aria-label="Previous review"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>

                  <button
                    type="button"
                    onClick={showNextReview}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-tdf-navy transition hover:border-[#E63B2E] hover:bg-[#E63B2E] hover:text-white"
                    aria-label="Next review"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </article>

            <div className="mt-6 flex justify-center gap-2">
              {customerReviews.map((review, index) => (
                <button
                  key={review.name}
                  type="button"
                  onClick={() => setActiveReview(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    activeReview === index
                      ? "w-8 bg-[#E63B2E]"
                      : "w-2.5 bg-slate-300 hover:bg-slate-400"
                  }`}
                  aria-label={`Show review from ${review.name}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="mb-12 text-center">
            <CircleHelp className="mx-auto mb-4 h-9 w-9 text-[#E63B2E]" />

            <h2 className="tdf-heading text-3xl text-tdf-navy md:text-5xl">
              Where should I start?
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((item) => (
              <article
                key={item.q}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="tdf-heading text-xl text-tdf-navy">
                  {item.q}
                </h3>

                <p className="mt-3 leading-relaxed text-slate-600">
                  {item.a}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#07111F] py-20 text-white">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <div className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#16b9c8]">
            <ShieldCheck className="h-4 w-4" />
            Understand the role · prepare with purpose
          </div>

          <h2 className="tdf-heading text-3xl text-white md:text-5xl">
            Begin with the right information.
          </h2>

          <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-slate-300">
            Learn what the job involves, understand how to enter the industry
            and only choose preparation support once you know what you need.
          </p>

          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link
              to="/what-does-a-train-driver-do"
              className="btn-pill btn-primary"
            >
              Explore the train driver role
              <ArrowRight className="h-4 w-4" />
            </Link>

            <Link
              to="/products"
              className="btn-pill btn-outline-white"
            >
              View all products and services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}